import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OnboardTenantResponse } from '@yawi/types';
import { useServices, useToast } from '@/app/providers';
import { WizardProgress, WizardNavigation, type WizardStep } from '@/core/ui/components';
import {
  BasicInfoStep,
  AdminAccountStep,
  PlanSelectionStep,
  BrandingStep,
  SettingsStep,
  SummaryStep,
} from './steps';
import { initialFormData, type WizardFormData } from './types';
import { TenantCreatedModal } from '../TenantCreatedModal';

const STEPS: WizardStep[] = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'admin', label: 'Admin' },
  { id: 'plan', label: 'Plan' },
  { id: 'branding', label: 'Branding' },
  { id: 'settings', label: 'Settings' },
  { id: 'summary', label: 'Confirm' },
];

interface TenantWizardProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

export function TenantWizard({ onComplete, onCancel }: TenantWizardProps) {
  const navigate = useNavigate();
  const { tenantRepository } = useServices();
  const { showToast } = useToast();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(initialFormData);
  const [stepValidity, setStepValidity] = useState<boolean[]>([false, false, true, true, true, true]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [onboardResult, setOnboardResult] = useState<OnboardTenantResponse | null>(null);

  const handleFormChange = useCallback((data: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  const handleValidChange = useCallback((isValid: boolean) => {
    setStepValidity((prev) => {
      const newValidity = [...prev];
      newValidity[currentStep] = isValid;
      return newValidity;
    });
  }, [currentStep]);

  const handleGoToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/tenants');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await tenantRepository.onboard({
        name: formData.name,
        slug: formData.slug,
        planId: formData.planId,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        settings: {
          ...formData.settings,
          branding: formData.branding,
        },
      });

      // Store result to show in modal
      setOnboardResult(result);
      showToast('success', 'Tenant creado exitosamente');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al crear tenant';
      showToast('error', message);
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setOnboardResult(null);
    if (onComplete) {
      onComplete();
    } else {
      navigate('/tenants');
    }
  };

  const renderStep = () => {
    const stepProps = {
      formData,
      onChange: handleFormChange,
      onValidChange: handleValidChange,
      errors,
    };

    switch (currentStep) {
      case 0:
        return <BasicInfoStep {...stepProps} />;
      case 1:
        return <AdminAccountStep {...stepProps} />;
      case 2:
        return <PlanSelectionStep {...stepProps} />;
      case 3:
        return <BrandingStep {...stepProps} />;
      case 4:
        return <SettingsStep {...stepProps} />;
      case 5:
        return <SummaryStep {...stepProps} onGoToStep={handleGoToStep} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto">
        <WizardProgress
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={handleGoToStep}
          stepValidity={stepValidity}
        />

        <div className="mt-8 p-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          {renderStep()}
        </div>

        <div className="mt-6">
          <WizardNavigation
            currentStep={currentStep}
            totalSteps={STEPS.length}
            onPrev={() => setCurrentStep((s) => s - 1)}
            onNext={() => setCurrentStep((s) => s + 1)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            canGoNext={stepValidity[currentStep]}
            isSubmitting={isSubmitting}
            submitLabel="Crear Tenant"
            cancelLabel="Cancelar"
            prevLabel="Anterior"
            nextLabel="Siguiente"
          />
        </div>
      </div>

      {/* Success Modal with Credentials */}
      {onboardResult && (
        <TenantCreatedModal
          isOpen={true}
          onClose={handleCloseSuccessModal}
          result={onboardResult}
        />
      )}
    </>
  );
}
